import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import useSWR from 'swr';
import { useEventOpenGraph } from '@/hooks/use-event-open-graph';
import axios from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useUser } from '@/hooks/use-user';
import { toast } from 'sonner';
import { type FormField } from '@/types';
import MyTicketDialog from '@/components/events/MyTicketDialog';
import CheckoutDialog from '@/components/events/CheckoutDialog';
import MyTicketsListDialog from '@/components/events/MyTicketsListDialog';
import {
  canInstantSelfRegister,
  getInstantSelfRegisterTicket,
  getSelfRegisterTicket,
  isTicketEnabled,
  isGoingIntent,
  memberCanPurchaseTicket,
  resolveGoingRegistrationAction,
  isEventEnded,
  shouldShowQuickRegistrationUI,
  stripGoingFromPath,
} from '@/lib/event-registration-eligibility';
import { instantSelfRegister } from '@/lib/event-instant-registration';

import { StoredMarketingReport } from '@/components/events/MarketingReportCard';
import { Banner } from './Banner';
import { EventHeader } from './EventHeader';
import { EventDescription } from './EventDescription';
import { DateLocationCard } from './DateLocationCard';
import { TicketsList } from './TicketsList';
import { QuickRegistration } from './QuickRegistration';
import { RegisteredCard } from './RegisteredCard';
import { RegistrationClosedCard } from './RegistrationClosedCard';
import { EventEndedCard } from './EventEndedCard';
import type { EventHeaderStatus } from './EventHeader';
import { MobileQuickRegisterBar } from './MobileQuickRegisterBar';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { EventsPageShell } from '../components/EventsPageShell';
import { ConclaveExperienceCard } from '@/components/conclave/ConclaveExperienceCard';

interface Ticket {
  id: string;
  name: string;
  description: string | null;
  price: number;
  capacity: number;
  enabled?: boolean;
  formSchema: FormField[] | null;
  type: 'MEMBER_ONLY' | 'MEMBER_GUEST' | 'OPEN_FOR_ALL';
  allowedAttendeeTypes?: string[];
}

interface EventRegistration {
  id: string;
  status: 'PENDING' | 'CONFIRMED' | 'ATTENDED' | 'CANCELLED';
  ticketId: string;
  attendeeName?: string;
  userId?: string | null;
}

interface Event {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  slug: string | null;
  banner: string | null;
  checkoutBgImage?: string | null;
  locationType: 'PHYSICAL' | 'VIRTUAL' | 'TO_BE_DECIDED';
  address: string | null;
  virtualUrl: string | null;
  tickets: Ticket[];
  isAdmin: boolean;
  isRegistered: boolean;
  isMember: boolean;
  isRegistrationClosed: boolean;
  allowDuplicatePhone: boolean;
  communityId: string | null;
  eventType?: 'BUSINESS' | 'SOCIAL' | 'CONCLAVE' | null;
  registrations: EventRegistration[];
}

const fetcher = (url: string) => axios.get(url).then((res) => res.data);

export default function EventDetailsPage() {
  const { eventId, communityId: routeCommunityId } = useParams<{
    eventId: string;
    communityId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [showTicketsListDialog, setShowTicketsListDialog] = useState(false);
  const [selectedRegistrationId, setSelectedRegistrationId] = useState<string | null>(null);
  const [cart, setCart] = useState<Array<{ ticket: Ticket; count: number }>>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [lockSelfCheckout, setLockSelfCheckout] = useState(false);
  const [ticketOpened, setTicketOpened] = useState(false);
  const [isInstantRegistering, setIsInstantRegistering] = useState(false);
  const goingHandledRef = useRef(false);
  const loginRedirectAttemptedRef = useRef(false);
  const instantRegisterInFlight = useRef(false);

  const { data, error, isLoading, mutate } = useSWR<{ event: Event }>(
    eventId ? `/api/events/${eventId}` : null,
    fetcher
  );

  useEventOpenGraph(
    data?.event
      ? {
          title: data.event.title,
          description: data.event.description,
          image: data.event.banner,
          slug: data.event.slug || data.event.id,
        }
      : null
  );

  const openTicketFromRegistrations = useCallback((registrations: EventRegistration[]) => {
    if (registrations.length > 1) {
      setShowTicketsListDialog(true);
    } else if (registrations.length === 1) {
      setSelectedRegistrationId(registrations[0].id);
      setShowTicketDialog(true);
    }
  }, []);

  const runInstantSelfRegister = useCallback(
    async (event: Event, ticket: Ticket) => {
      if (instantRegisterInFlight.current) return false;

      instantRegisterInFlight.current = true;
      setIsInstantRegistering(true);

      try {
        const success = await instantSelfRegister(event.id, ticket.id);
        if (!success) return false;

        toast.success("You're registered!");
        const updated = await mutate();
        const registrations = updated?.event?.registrations ?? [];
        openTicketFromRegistrations(registrations);
        return true;
      } finally {
        instantRegisterInFlight.current = false;
        setIsInstantRegistering(false);
      }
    },
    [mutate, openTicketFromRegistrations]
  );

  const startCheckoutWithTicket = useCallback(
    (ticket: Ticket, options?: { lockSelf?: boolean }) => {
      setCart([{ ticket, count: 1 }]);
      setLockSelfCheckout(Boolean(options?.lockSelf));
      setIsCartOpen(true);
    },
    []
  );

  useEffect(() => {
    if (data?.event?.registrations && location.search.includes('ticket=true') && !ticketOpened) {
      setTimeout(() => {
        openTicketFromRegistrations(data.event.registrations);
        setTicketOpened(true);
      }, 0);
      window.history.replaceState({}, '', location.pathname);
    }
  }, [data?.event, location.search, ticketOpened, location.pathname, openTicketFromRegistrations]);

  useEffect(() => {
    if (!data?.event || !isGoingIntent(location.search) || goingHandledRef.current) return;

    const { event } = data;

    if (!user) {
      if (!loginRedirectAttemptedRef.current) {
        loginRedirectAttemptedRef.current = true;
        navigate('/login', {
          state: { from: `${location.pathname}${location.search}` },
        });
      }
      return;
    }

    const action = resolveGoingRegistrationAction(event, user);
    goingHandledRef.current = true;

    if (!action) {
      window.history.replaceState({}, '', stripGoingFromPath(location.pathname, location.search));
      return;
    }

    window.history.replaceState({}, '', stripGoingFromPath(location.pathname, location.search));

    if (action.type === 'instant') {
      const ticket = event.tickets.find((t) => t.id === action.ticket.id);
      if (ticket) {
        void runInstantSelfRegister(event, ticket);
      }
      return;
    }

    if (action.type === 'checkout') {
      const ticket = event.tickets.find((t) => t.id === action.ticket.id);
      if (ticket) {
        const isSelfGoing = getSelfRegisterTicket(event)?.id === ticket.id;
        startCheckoutWithTicket(ticket, { lockSelf: isSelfGoing });
      }
      return;
    }

    toast.info(action.message);
  }, [
    data,
    data?.event,
    location.pathname,
    location.search,
    navigate,
    runInstantSelfRegister,
    startCheckoutWithTicket,
    user,
  ]);

  const handleUpdateCart = (ticket: Ticket, delta: number) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.ticket.id === ticket.id);

      if (!existing) {
        if (delta > 0) {
          toast.success(`Added ${ticket.name} to cart`);
          return [...prev, { ticket, count: delta }];
        }
        return prev;
      }

      const newCount = existing.count + delta;
      if (newCount <= 0) {
        toast.info(`Removed ${ticket.name} from cart`);
        return prev.filter((item) => item.ticket.id !== ticket.id);
      }

      return prev.map((item) =>
        item.ticket.id === ticket.id ? { ...item, count: newCount } : item
      );
    });
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    setIsCartOpen(true);
  };

  const handleViewTicket = () => {
    if (data?.event?.registrations) {
      openTicketFromRegistrations(data.event.registrations);
    }
  };

  const handleSelectRegistration = (id: string) => {
    setSelectedRegistrationId(id);
    setShowTicketsListDialog(false);
    setShowTicketDialog(true);
  };

  const handleShare = async () => {
    const shareMessage = `Hi! 👋 You're invited to join us for "${event?.title}". We'd love to have you there! Please use the following link to register:`;
    const shareUrl = `${window.location.origin}/e/${event?.slug || event?.id}`;
    const fullMessage = `${shareMessage}\n\n${shareUrl}`;

    const shareData = {
      title: event?.title,
      text: shareMessage,
      url: shareUrl,
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(fullMessage);
        toast.success('Invitation copied to clipboard');
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('Error sharing:', err);
        await navigator.clipboard.writeText(fullMessage);
        toast.success('Invitation copied to clipboard');
      }
    }
  };

  const handleNavigateBack = () => {
    if (routeCommunityId) {
      navigate(`/${routeCommunityId}/events`);
      return;
    }
    if (data?.event?.communityId == null) {
      navigate('/events');
      return;
    }
    navigate('/');
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (error || !data?.event) {
    return <ErrorState routeCommunityId={routeCommunityId} onNavigateBack={handleNavigateBack} />;
  }

  const { event } = data;
  const communityId = routeCommunityId || event.communityId;

  const startDate = new Date(event.startDate);
  const endDate = new Date(event.endDate);

  const availableTickets = event.tickets.filter(isTicketEnabled);
  const instantTicket = getInstantSelfRegisterTicket(
    { ...event, tickets: availableTickets },
    user
  );
  const selfTicket = getSelfRegisterTicket({ ...event, tickets: availableTickets });
  const instantEligible = canInstantSelfRegister({ ...event, tickets: availableTickets }, user);
  const showQuickReg = shouldShowQuickRegistrationUI(
    { ...event, tickets: availableTickets },
    user
  );
  const ended = isEventEnded(event);
  const guestTicketsAvailable = availableTickets.some((t) =>
    (t.allowedAttendeeTypes ?? ['GUEST']).includes('GUEST')
  );
  const showTicketsList =
    !event.isRegistrationClosed && !ended && (!event.isRegistered || guestTicketsAvailable);
  const headerStatus: EventHeaderStatus = event.isRegistered
    ? 'going'
    : ended
      ? 'ended'
      : event.isRegistrationClosed
        ? 'closed'
        : availableTickets.length > 0
          ? 'open'
          : 'free';
  const hasRegistrations = event.registrations.length > 0;
  const quickRegTicket =
    instantTicket ??
    selfTicket ??
    (availableTickets.length === 1 ? availableTickets[0] : null);
  const canPurchaseQuickReg = quickRegTicket
    ? memberCanPurchaseTicket(quickRegTicket, event.isMember)
    : false;
  const quickRegLabel = instantEligible || selfTicket ? "I'm Going" : 'Register Now';

  const loginRedirectPath = `${location.pathname}${location.search}`;

  const handleQuickRegister = async () => {
    if (!quickRegTicket) return;

    if (!user) {
      navigate('/login', { state: { from: loginRedirectPath } });
      return;
    }

    if (!canPurchaseQuickReg) {
      toast.info('You must be a member of this community to purchase this ticket.');
      return;
    }

    if (instantTicket) {
      const ticket = event.tickets.find((t) => t.id === instantTicket.id);
      if (ticket) {
        await runInstantSelfRegister(event, ticket);
      }
      return;
    }

    const checkoutTicket = event.tickets.find((t) => t.id === quickRegTicket.id);
    if (checkoutTicket) {
      startCheckoutWithTicket(checkoutTicket, { lockSelf: Boolean(selfTicket) });
    }
  };

  const handleLoginRequired = () => {
    if (!user) {
      navigate('/login', { state: { from: loginRedirectPath } });
    } else {
      toast.info('You must be a member of this community to purchase this ticket.');
    }
  };

  return (
    <EventsPageShell
      maxWidthClassName="max-w-4xl"
      outerClassName={showQuickReg ? 'pb-28 md:pb-6' : undefined}
    >
      <div className="w-full px-4 md:px-6 lg:px-8 py-4 md:py-6">
        <Button
          variant="ghost"
          className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
          onClick={handleNavigateBack}
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to {routeCommunityId ? 'Events' : 'Home'}
        </Button>

        <Banner
          banner={event.banner}
          title={event.title}
          isAdmin={event.isAdmin}
          communityId={communityId}
          eventId={event.id}
          onShare={handleShare}
        />
        {event.isAdmin ? <StoredMarketingReport eventId={event.id} /> : null}

        <div className="grid min-w-0 grid-cols-1 gap-4 md:gap-8 xl:grid-cols-3">
          <div className="min-w-0 xl:col-span-2 xl:col-start-1 xl:row-start-1">
            <EventHeader
              title={event.title}
              status={headerStatus}
              locationType={event.locationType}
            />
          </div>

          <div className="flex min-w-0 flex-col gap-6 xl:col-start-3 xl:row-span-2 xl:row-start-1">
            <DateLocationCard
              startDate={startDate}
              endDate={endDate}
              locationType={event.locationType}
              address={event.address}
            />

            {event.eventType === 'CONCLAVE' && user && (event.isRegistered || event.isAdmin) ? (
              <div className="order-first xl:order-none">
                <ConclaveExperienceCard
                  isRegistered={
                    event.isRegistered ||
                    event.registrations.some(
                      (r) => r.userId === user.id && r.status !== 'CANCELLED'
                    )
                  }
                  isAdmin={event.isAdmin}
                  communityId={routeCommunityId || event.communityId}
                  eventId={event.id}
                  eventSlug={event.slug}
                />
              </div>
            ) : null}

            {hasRegistrations ? (
              <RegisteredCard
                eventId={event.id}
                registrationsCount={event.registrations.length}
                isSelfAttending={event.isRegistered}
                onViewTicket={handleViewTicket}
              />
            ) : null}

            {event.isRegistrationClosed && !hasRegistrations ? <RegistrationClosedCard /> : null}
            {ended && !event.isRegistered ? <EventEndedCard /> : null}

            {!event.isRegistrationClosed && !ended && (
              <>
                {showQuickReg &&
                  (isInstantRegistering ? (
                    <div className="flex items-center justify-center gap-2 rounded-lg border border-border bg-muted/30 p-6 text-sm text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      Registering you...
                    </div>
                  ) : (
                    <QuickRegistration
                      onQuickRegister={() => void handleQuickRegister()}
                      label={quickRegLabel}
                      isLoading={isInstantRegistering}
                      disabled={!canPurchaseQuickReg}
                    />
                  ))}

                {showTicketsList ? (
                  <TicketsList
                    tickets={availableTickets}
                    cart={cart}
                    isMember={event.isMember}
                    isRegistered={event.isRegistered}
                    onCheckout={handleCheckout}
                    onUpdateCart={handleUpdateCart}
                    onLoginRequired={handleLoginRequired}
                  />
                ) : null}
              </>
            )}
          </div>

          <div className="min-w-0 xl:col-span-2 xl:col-start-1 xl:row-start-2">
            <EventDescription description={event.description} />
          </div>
        </div>

        {showQuickReg && !event.isRegistrationClosed && !isInstantRegistering && (
          <MobileQuickRegisterBar
            onQuickRegister={() => void handleQuickRegister()}
            routeCommunityId={routeCommunityId}
            label={quickRegLabel}
            isLoading={isInstantRegistering}
            disabled={!canPurchaseQuickReg}
          />
        )}

        <CheckoutDialog
          open={isCartOpen}
          onOpenChange={(open) => {
            setIsCartOpen(open);
            if (!open) {
              setLockSelfCheckout(false);
            }
          }}
          cart={cart.map((item) => ({
            ...item,
            ticket: {
              ...item.ticket,
              description: item.ticket.description || undefined,
              formSchema: item.ticket.formSchema || undefined,
            },
          }))}
          eventId={event.id}
          isMember={event.isMember}
          isRegistrationClosed={event.isRegistrationClosed}
          allowDuplicatePhone={event.allowDuplicatePhone}
          onSuccess={() => {
            setCart([]);
            mutate();
          }}
          checkoutBgImage={event.checkoutBgImage}
          communityId={communityId!}
          lockSelfAttendee={lockSelfCheckout}
          selfRegistered={event.isRegistered}
        />

        {event.registrations.length > 0 && selectedRegistrationId && (
          <MyTicketDialog
            open={showTicketDialog}
            onOpenChange={setShowTicketDialog}
            registrationId={selectedRegistrationId}
            eventId={event.id}
            onCancelSuccess={() => {
              mutate();
              setSelectedRegistrationId(null);
            }}
            registrations={event.registrations}
            onNavigate={(id) => setSelectedRegistrationId(id)}
            onBackToList={() => {
              setShowTicketDialog(false);
              setShowTicketsListDialog(true);
            }}
          />
        )}

        <MyTicketsListDialog
          open={showTicketsListDialog}
          onOpenChange={setShowTicketsListDialog}
          registrations={event.registrations}
          tickets={event.tickets}
          onSelect={handleSelectRegistration}
        />
      </div>
    </EventsPageShell>
  );
}
